import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Avatar, Box, Button, Card, CardContent, Grid, Skeleton, Stack, Tab, Tabs, Typography, TextField, Tooltip, IconButton, TablePagination } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import PictureAsPdfRounded from "@mui/icons-material/PictureAsPdfRounded";
import HourglassTopRounded from "@mui/icons-material/HourglassTopRounded";
import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import HighlightOffRounded from "@mui/icons-material/HighlightOffRounded";
import PersonSearchOutlined from "@mui/icons-material/PersonSearchOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import { staffCategory, schoolStaff, schoolUsersId, schoolViewPath, sameId } from "../utils/schoolStaff";
import { post } from "../services/api";
import ENDPOINTS from "../utils/constants/endpoints";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fetchSchoolStaffReport, schoolReportScope } from "../redux/slices/driverSlice";
import useIsMobile from "../hooks/useIsMobile";
import { gridSx } from "../components/FilterList";
import { DASHBOARD_COLORS as C, STATUS_COLORS, focusRing, hasCount } from "../components/DashboardCard";
import { backButtonStyle, cardStyle, InfoField, pageStyle } from "../components/busDetails/detailLayout";

// The shared page style tints every button blue; this page colours its own buttons.
const { "& .MuiButton-root": _pageButtons, ...basePage } = pageStyle;
const page = { ...basePage, bgcolor: C.page, color: C.navy };
const card = { ...cardStyle, height: "auto", borderColor: C.border };
const MIS_PATH = "/schools";

const STATUSES = [
  { key: "pending", field: "pending", Icon: HourglassTopRounded },
  { key: "approved", field: "approve", Icon: CheckCircleOutlineRounded },
  { key: "rejected", field: "reject", Icon: HighlightOffRounded },
];

const RECORD_TABS = [
  { type: "Driver", noun: "driver", plural: "Drivers" },
  { type: "Conductor", noun: "conductor", plural: "Conductors" },
  { type: "Attendant", noun: "attendant", plural: "Attendants" },
];

const exportButtonSx = {
  height: 38, flexShrink: 0, px: { xs: 1.5, sm: 2 }, borderRadius: "10px", textTransform: "none", fontWeight: 600, fontSize: 14,
  color: "#fff", bgcolor: C.primary, boxShadow: "0 1px 2px rgba(16,42,67,.12)",
  "&:hover": { bgcolor: C.primaryHover, boxShadow: "0 4px 10px rgba(37,99,235,.25)" },
  "&.Mui-disabled": { color: "rgba(255,255,255,.85)", bgcolor: "#93C5FD" },
  ...focusRing,
};

const tabsSx = {
  minHeight: 36, p: 0.5, bgcolor: "#F1F5F9", border: `1px solid ${C.border}`, borderRadius: "12px", maxWidth: "100%",
  "& .MuiTabs-indicator": { display: "none" },
  "& .MuiTab-root": { minHeight: 34, py: 0, px: 1.5, borderRadius: "9px", textTransform: "none", fontWeight: 600, fontSize: 13.5, color: "#334155", ...focusRing },
  "& .MuiTab-root.Mui-selected": { color: "#fff", bgcolor: C.primary, boxShadow: "0 1px 2px rgba(37,99,235,.35)" },
};

function StatusCard({ status, value }) {
  const { label, color } = STATUS_COLORS[status.key];
  return (
    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, gap: { xs: 0.75, sm: 1.25 }, p: { xs: 1.25, sm: 1.5 }, minWidth: 0, borderRadius: "12px", border: `1px solid ${color}33`, bgcolor: `${color}0D` }}>
      <Avatar variant="rounded" sx={{ width: 34, height: 34, borderRadius: "9px", bgcolor: `${color}1F`, color }}>
        <status.Icon fontSize="small" />
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 12, color: C.muted, lineHeight: 1.3 }}>{label}</Typography>
        <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2, color, fontVariantNumeric: "tabular-nums" }}>{hasCount(value) ? value : "—"}</Typography>
      </Box>
    </Box>
  );
}

// The staff table never shows undefined, null or an object for a missing field.
const text = (value) => (value == null || typeof value === "object" || String(value).trim() === "" ? "-" : String(value));

function EmptyRecords({ noun, total }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ p: 2 }}>
      <PersonSearchOutlined sx={{ color: C.muted }} />
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.navy }}>No {noun} records found</Typography>
        <Typography sx={{ fontSize: 13, color: C.muted }}>
          {Number(total) === 0 ? "This school currently has no associated records." : `No ${noun}s are linked to this school yet.`}
        </Typography>
      </Box>
    </Stack>
  );
}

function RecordCards({ rows, openPerson }) {
  return (
    <Box component="ul" aria-label="Records" sx={{ listStyle: "none", m: 0, p: 0 }}>
      {rows.map((d, i) => (
        <Box component="li" key={d.driver_id ?? i} sx={{ px: 1.5, py: 1.25, borderTop: i ? `1px solid ${C.border}` : "none", minWidth: 0 }}>
          <Button onClick={() => openPerson(d)} disabled={d.driver_id == null} sx={{ p: 0, textTransform: "none" }}>{d.contact_person || "Name not updated"}</Button>
          <Tooltip title="View details"><IconButton aria-label={`View ${d.contact_person}`} onClick={() => openPerson(d)} disabled={d.driver_id == null}><VisibilityOutlined /></IconButton></Tooltip>
          <Typography sx={{ fontSize: 13, color: "#334155", overflowWrap: "anywhere" }}>
            Mobile: {text(d.mobile1)} · DL No: {text(d.dl_no)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function SchoolView() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const stateSchool = location.state?.school;
  const urlSchoolId = new URLSearchParams(location.search).get("school_id");
  // Without history state (a new tab or a copied link), the school row is looked up again by its URL id.
  const [restored, setRestored] = useState({ school: null, loading: false, failed: false });
  const school = stateSchool ?? restored.school;
  // API/Reports/driver takes the school_id of the API/Reports/school row, as that row carries it.
  const reportSchoolId = school?.school_id == null || String(school.school_id).trim() === "" ? null : String(school.school_id);
  const { list, loading, error, scope } = useSelector((state) => state.drivers);
  // Only the staff report requested for this school is shown; another screen's list is never reused.
  const scoped = reportSchoolId != null && scope === schoolReportScope(reportSchoolId);
  const drivers = scoped && Array.isArray(list) ? list : [];
  const [tab, setTab] = useState(location.state?.staffView?.tab ?? 0);
  const [paginationModel, setPaginationModel] = useState(location.state?.staffView?.paginationModel ?? { page: 0, pageSize: 10 })
  const [search, setSearch] = useState(location.state?.staffView?.search ?? "");;

  const loadStaff = useCallback(() => {
    if (reportSchoolId) dispatch(fetchSchoolStaffReport({ schoolId: reportSchoolId }));
  }, [dispatch, reportSchoolId]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  useEffect(() => {
    if (stateSchool || !urlSchoolId) return undefined;
    let active = true;
    setRestored({ school: null, loading: true, failed: false });
    post(ENDPOINTS.MASTER.SCHOOL_LIST, { token: localStorage.getItem("token"), format: "Json" })
      .then((res) => {
        if (!active) return;
        if ((res?.status && res.status !== "success") || !Array.isArray(res?.data)) throw new Error("Invalid school report response");
        setRestored({ school: res.data.find((row) => schoolUsersId(row) && sameId(schoolUsersId(row), urlSchoolId)) ?? null, loading: false, failed: false });
      })
      .catch(() => active && setRestored({ school: null, loading: false, failed: true }));
    return () => { active = false; };
  }, [stateSchool, urlSchoolId]);

  // MIS Reports kept its tab, search and page on its history entry; without history, open the School tab.
  const goBack = () => (location.key && location.key !== "default" ? navigate(-1) : navigate(MIS_PATH, { state: { misReport: { tab: "School" } } }));
  const back = (
    <Button variant="text" size="small" onClick={goBack} startIcon={<ArrowBackRounded sx={{ fontSize: 18 }} />} sx={{ ...backButtonStyle, textTransform: "none", color: C.primary }}>
      Back to School List
    </Button>
  );

  if (!school) {
    return (
      <Box sx={page}>
        {back}
        {restored.loading ? (
          <Box role="status" aria-label="Loading school" sx={{ p: 1.5 }}>{[1, 2, 3].map((i) => <Skeleton key={i} height={36} />)}</Box>
        ) : restored.failed ? (
          <Alert severity="error">Unable to load this school. Check your connection, then return to MIS Reports and open the school again.</Alert>
        ) : (
          <Alert severity="info">No school data found. Return to MIS Reports and open a school from the School tab.</Alert>
        )}
      </Box>
    );
  }

  const schoolDrivers = schoolStaff(drivers, school);
  const lists = RECORD_TABS.map(({ type }) => schoolDrivers.filter((d) => staffCategory(d) === type));
  const categoryList = lists[tab] || [];
  const activeList = categoryList.filter(d => [d.contact_person, d.register_no, d.mobile1].some(value => String(value ?? "").toLowerCase().includes(search.toLowerCase().trim())));
  const current = RECORD_TABS[tab] || RECORD_TABS[0];
  // Until this school's request has started, the store may still hold another screen's list.
  const waiting = Boolean(reportSchoolId) && (!scoped || loading);
  const staffError = !reportSchoolId ? "missing" : scoped && error ? (error.sessionExpired ? "session" : error.kind === "network" ? "network" : "failed") : null;
  const openPerson = (person) => {
    if (person.driver_id == null) return;
    const state = { ...location.state, school, staffView: { tab, search, paginationModel } };
    navigate(schoolViewPath(school), { replace: true, state });
    navigate(`/drivers/${encodeURIComponent(person.driver_id)}`, { state: { schoolReturn: state } });
  };
  const columns = [
    { field: "contact_person", headerName: "Name", flex: 1.4, minWidth: 180, renderCell: ({ row }) => <Button onClick={() => openPerson(row)} disabled={row.driver_id == null} sx={{ textTransform: "none" }}>{row.contact_person || "Name not updated"}</Button> },
    ...[["register_no", "Registration No"], ["designation", "Designation"], ["mobile1", "Mobile"], ["police_station_level1_status", "Police Verification Status"]].map(([field, headerName]) => ({ field, headerName, flex: 1, minWidth: 140, valueGetter: (_, row) => text(row[field]) })),
    { field: "action", headerName: "Action", width: 80, sortable: false, renderCell: ({ row }) => <Tooltip title="View details"><IconButton aria-label={`View ${row.contact_person}`} disabled={row.driver_id == null} onClick={() => openPerson(row)}><VisibilityOutlined /></IconButton></Tooltip> },
  ];

  const exportPDF = () => {
    const doc = new jsPDF();

    doc.text(
      `${school.school} - ${current.type} List`,
      14,
      15
    );

    autoTable(doc, {
      startY: 20,
      head: [current.type === "Attendant" ? ["Name", "Mobile", "Registration No"] : ["Name", "Mobile", "DL No"]],
      body: categoryList.map((d) => [
        text(d.contact_person),
        text(d.mobile1),
        text(current.type === "Attendant" ? d.register_no : d.dl_no)
      ]),
    });

    doc.save(
      `${school.school}_${current.plural.toLowerCase()}.pdf`
    );
  };

  const changeTab = (_, next) => {
    setTab(next);
    setSearch("");
    setPaginationModel((model) => ({ ...model, page: 0 }));
  };

  let records;
  if (waiting) {
    records = <Box role="status" aria-label="Loading records" sx={{ p: 1.5 }}>{[1, 2, 3].map((i) => <Skeleton key={i} height={36} />)}</Box>;
  } else if (staffError === "missing") {
    records = (
      <Box sx={{ p: 1.5 }}>
        <Alert severity="error">This school record has no school ID, so its driver, conductor and attendant records cannot be loaded.</Alert>
      </Box>
    );
  } else if (staffError === "session") {
    records = (
      <Box sx={{ p: 1.5 }}>
        <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => navigate("/login", { replace: true })}>Sign in</Button>}>
          Your session has expired. Please sign in again to view this school's records.
        </Alert>
      </Box>
    );
  } else if (staffError) {
    records = (
      <Box sx={{ p: 1.5 }}>
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={loadStaff}>Retry</Button>}>
          {staffError === "network"
            ? "Unable to load driver, conductor and attendant records. Check your connection and try again."
            : "Unable to load driver, conductor and attendant records right now. Please try again."}
        </Alert>
      </Box>
    );
  } else if (!activeList.length) {
    records = <EmptyRecords noun={current.noun} total={school.total} />;
  } else if (isMobile) {
    records = <><RecordCards rows={activeList.slice(paginationModel.page * paginationModel.pageSize, (paginationModel.page + 1) * paginationModel.pageSize)} openPerson={openPerson} /><TablePagination component="div" count={activeList.length} page={Math.min(paginationModel.page, Math.max(0, Math.ceil(activeList.length / paginationModel.pageSize) - 1))} rowsPerPage={paginationModel.pageSize} rowsPerPageOptions={[10, 20, 50]} onPageChange={(_, page) => setPaginationModel(m => ({ ...m, page }))} onRowsPerPageChange={e => setPaginationModel({ page: 0, pageSize: Number(e.target.value) })} /></>;
  } else {
    records = (
      <DataGrid
        rows={activeList}
        getRowId={(row) => row.driver_id ?? `${row.contact_person}-${row.mobile1}`}
        columns={columns}
        rowHeight={48}
        columnHeaderHeight={42}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 20, 50]}
        hideFooter={activeList.length <= 10}
        disableColumnFilter
        disableRowSelectionOnClick
        aria-label={`${current.plural} of ${school.school}`}
        sx={{ ...gridSx, minHeight: 0 }}
      />
    );
  }

  return (
    <Box sx={page}>
      {back}

      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5} mb={1.5}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h1" fontWeight={700} sx={{ fontSize: { xs: 20, sm: 23 }, lineHeight: 1.25, color: C.navy, overflowWrap: "anywhere" }}>
            {school.school || "School name not updated"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: C.muted, overflowWrap: "anywhere" }}>
            Registration No: {school.register_no || "Not updated"}
          </Typography>
        </Box>
        <Button onClick={exportPDF} disabled={waiting || Boolean(staffError) || !categoryList.length} startIcon={<PictureAsPdfRounded />} aria-label={`Export ${current.plural} PDF`} sx={exportButtonSx}>
          Export PDF
        </Button>
      </Stack>

      <Card elevation={0} sx={card}>
        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <Grid container columnSpacing={2} rowSpacing={1}>
                <Grid size={{ xs: 6, md: 12 }}><InfoField label="Registration No" value={school.register_no} /></Grid>
                <Grid size={{ xs: 6, md: 12 }}><InfoField label="Total Records" value={school.total} /></Grid>
              </Grid>
            </Grid>
            <Grid size={{ xs: 12, md: 9 }}>
              <Box role="group" aria-label="Verification status" sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: { xs: 1, sm: 1.25 } }}>
                {STATUSES.map((status) => <StatusCard key={status.key} status={status} value={school[status.field]} />)}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card elevation={0} sx={{ ...card, mt: 1.75, overflow: "hidden" }}>
        <Box sx={{ p: { xs: 1.25, sm: 1.5 }, borderBottom: `1px solid ${C.border}` }}>
          <Tabs value={tab} onChange={changeTab} variant="scrollable" scrollButtons={false} aria-label="Record type" sx={tabsSx}>
            {RECORD_TABS.map(({ plural }, i) => <Tab key={plural} label={`${plural} (${waiting ? "…" : lists[i].length})`} />)}
          </Tabs>
        </Box>
        <TextField size="small" label="Search name, registration or mobile" value={search} onChange={e => { setSearch(e.target.value); setPaginationModel(m => ({ ...m, page: 0 })); }} sx={{ m: 1.5, width: "min(90%, 380px)" }} />
        {records}
      </Card>
    </Box>
  );
}
